import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Plus, Trash2, ShieldCheck, ShieldAlert, FileText, Heart, Stethoscope, Pill, Salad, IdCard, Edit, FileVideo, MessageCircle, ChevronUp, ChevronDown, Camera, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { DogClipGallery } from '../../components/DogClipGallery';
import { useNavigate } from 'react-router-dom';

const fileToBase64 = (f) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(f);
});

const BLANK = {
  name:'', breed:'', age_years:'', weight_lbs:'', sex:'M', color:'',
  microchip_number:'', registration_number:'', spay_neuter:'intact',
  allergies:'', medications:'', dietary_notes:'',
  vet_name:'', vet_phone:'', vet_address:'',
  emergency_contact_name:'', emergency_contact_phone:'', emergency_contact_relationship:'',
  behavior_notes:'', training_needs:'', photo_base64:'',
};

export default function ClientDogs() {
  const [dogs, setDogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [vaxOpen, setVaxOpen] = useState(null);
  const [vaxList, setVaxList] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [vaxForm, setVaxForm] = useState({ vaccine_name:'Rabies', administered_date:'', expiration_date:'', veterinarian:'', document_base64:'', notes:'' });
  const [expandedDogId, setExpandedDogId] = useState(null);
  const navigate = useNavigate();
  // Refs for each dog's hidden <input type="file"> so the camera overlay
  // can trigger the native file picker.
  const dogPhotoRefs = useRef({});
  const [uploadingPhotoId, setUploadingPhotoId] = useState(null);

  const load = () => api.get('/dogs').then(r => setDogs(r.data));
  useEffect(() => { load(); }, []);

  /**
   * Owner self-upload of a dog's primary photo. Uploads to
   * /files/upload (purpose=dog_photo), then PATCHes the dog with
   * `photo_url` pointing at the new object-storage URL. PATCH /dogs
   * already does an owner-check so clients can only edit their own
   * pets.
   */
  const onDogPhotoPick = async (dogId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhotoId(dogId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', 'dog_photo');
      const { data: uploaded } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.patch(`/dogs/${dogId}`, { photo_url: uploaded.url });
      toast.success('Dog photo updated');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingPhotoId(null); }
  };

  const onPhoto = async (e) => { if (e.target.files?.[0]) setForm({...form, photo_base64: await fileToBase64(e.target.files[0])}); };
  const onVaxDoc = async (e) => { if (e.target.files?.[0]) setVaxForm({...vaxForm, document_base64: await fileToBase64(e.target.files[0])}); };

  const openCreate = () => { setEditingId(null); setForm(BLANK); setOpen(true); };
  const openEdit = (d) => {
    setEditingId(d.id);
    const v = { ...BLANK };
    Object.keys(BLANK).forEach(k => { if (d[k] !== undefined && d[k] !== null) v[k] = d[k] === null ? '' : d[k]; });
    v.age_years = d.age_years ?? '';
    v.weight_lbs = d.weight_lbs ?? '';
    setForm(v);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (payload.age_years === '') delete payload.age_years; else payload.age_years = parseFloat(payload.age_years);
      if (payload.weight_lbs === '') delete payload.weight_lbs; else payload.weight_lbs = parseFloat(payload.weight_lbs);
      if (editingId) {
        await api.patch(`/dogs/${editingId}`, payload);
        toast.success('Profile updated');
      } else {
        await api.post('/dogs', payload);
        toast.success('Dog profile added');
      }
      setOpen(false); setEditingId(null); setForm(BLANK); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this dog and all records?')) return;
    await api.delete(`/dogs/${id}`); toast.success('Deleted'); load();
  };

  const openVax = async (id) => {
    setVaxOpen(id);
    const { data } = await api.get('/vaccinations', { params: { dog_id: id } });
    setVaxList(data);
  };

  const submitVax = async (e) => {
    e.preventDefault();
    try {
      await api.post('/vaccinations', { ...vaxForm, dog_id: vaxOpen });
      toast.success('Vaccination saved');
      const { data } = await api.get('/vaccinations', { params: { dog_id: vaxOpen } });
      setVaxList(data);
      setVaxForm({ vaccine_name:'Rabies', administered_date:'', expiration_date:'', veterinarian:'', document_base64:'', notes:'' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const removeVax = async (id) => {
    await api.delete(`/vaccinations/${id}`);
    const { data } = await api.get('/vaccinations', { params: { dog_id: vaxOpen } });
    setVaxList(data); load();
  };

  const FormSection = ({ icon: Icon, title, children, accent = '#1B3A87' }) => (
    <div className='border border-slate-200 rounded-lg p-4 bg-white'>
      <div className='flex items-center gap-2 mb-3' style={{ color: accent }}>
        <Icon className='w-4 h-4' /><h4 className='font-display font-semibold text-sm uppercase tracking-wider'>{title}</h4>
      </div>
      {children}
    </div>
  );

  return (
    <div className='p-6 lg:p-8 max-w-6xl mx-auto'>
      <div className='flex items-center justify-between mb-6'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Pet Profiles</div>
          <h1 className='font-display text-3xl font-bold text-[#0B1220] mt-1'>My Dogs</h1>
          <p className='text-slate-500 text-sm mt-1'>Upload photos, log medical/behavioral details, and manage vaccinations — all linked to your account.</p>
        </div>
        <Button className='bg-[#1B3A87] hover:bg-[#16306F] text-white' onClick={openCreate} data-testid='add-dog-button'><Plus className='w-4 h-4 mr-2' /> Add Dog</Button>
      </div>

      {dogs.length === 0 ? (
        <Card className='p-10 text-center border border-dashed border-slate-300'>
          <div className='text-slate-500 mb-3'>No dogs yet. Add your first dog profile to start booking boarding.</div>
        </Card>
      ) : (
        <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-4' data-testid='dogs-grid'>
          {dogs.map((d) => (
            <Card key={d.id} className='overflow-hidden border border-slate-200 hover:shadow-md transition-shadow' data-testid={`dog-card-${d.id}`}>
              <div className='h-44 bg-slate-100 relative group'>
                {(d.photo_url || d.photo_base64) ? (
                  <img
                    src={d.photo_url || d.photo_base64}
                    alt={d.name}
                    className='w-full h-full object-cover'
                    data-testid={`dog-photo-${d.id}`}
                  />
                ) : (
                  <div className='w-full h-full flex items-center justify-center text-slate-400 font-display text-5xl'>{d.name[0]}</div>
                )}
                {/* Owner self-upload camera overlay */}
                <button
                  type='button'
                  onClick={() => dogPhotoRefs.current[d.id]?.click()}
                  className='absolute top-3 left-3 w-9 h-9 rounded-full bg-black/55 hover:bg-black/75 text-white backdrop-blur flex items-center justify-center shadow-md disabled:opacity-60'
                  disabled={uploadingPhotoId === d.id}
                  aria-label={`Change photo for ${d.name}`}
                  data-testid={`dog-photo-upload-btn-${d.id}`}
                >
                  {uploadingPhotoId === d.id ? <RotateCcw className='w-4 h-4 animate-spin' /> : <Camera className='w-4 h-4' />}
                </button>
                <input
                  ref={(el) => (dogPhotoRefs.current[d.id] = el)}
                  type='file'
                  accept='image/*'
                  className='hidden'
                  onChange={(e) => onDogPhotoPick(d.id, e)}
                  data-testid={`dog-photo-upload-input-${d.id}`}
                />
                {d.vax_compliant ? <Badge className='absolute top-3 right-3 bg-[#167A5A] text-white border-0'><ShieldCheck className='w-3 h-3 mr-1' /> VAX OK</Badge> :
                  <Badge className='absolute top-3 right-3 bg-[#B45309] text-white border-0'><ShieldAlert className='w-3 h-3 mr-1' /> VAX needed</Badge>}
                {d.assigned_trainer_name && (
                  <Badge className='absolute bottom-3 left-3 bg-[#1B3A87] text-white border-0'>Trainer: {d.assigned_trainer_name}</Badge>
                )}
              </div>
              <div className='p-4'>
                <div className='font-display font-bold text-lg'>{d.name}</div>
                <div className='text-xs text-slate-500 mt-0.5'>{[d.breed, d.sex === 'M' ? 'Male' : 'Female', d.age_years ? `${d.age_years}y` : null, d.weight_lbs ? `${d.weight_lbs}lb` : null].filter(Boolean).join(' · ')}</div>
                {d.microchip_number && <div className='text-[10px] text-slate-500 font-mono mt-1'>Chip: {d.microchip_number}</div>}
                {d.allergies && <div className='text-xs mt-2'><span className='font-semibold text-[#B42318]'>Allergies:</span> <span className='text-slate-700'>{d.allergies}</span></div>}
                {d.medications && <div className='text-xs mt-1'><span className='font-semibold text-[#B45309]'>Meds:</span> <span className='text-slate-700'>{d.medications}</span></div>}
                {d.vet_name && <div className='text-xs text-slate-600 mt-2'><Stethoscope className='w-3 h-3 inline mr-1' /> {d.vet_name}{d.vet_phone ? ` · ${d.vet_phone}` : ''}</div>}
                <div className='flex gap-2 mt-3'>
                  <Button size='sm' variant='outline' className='flex-1' onClick={() => openEdit(d)} data-testid={`edit-dog-${d.id}`}><Edit className='w-4 h-4 mr-1' /> Edit</Button>
                  <Button size='sm' variant='outline' onClick={() => openVax(d.id)} data-testid={`vax-button-${d.id}`}><FileText className='w-4 h-4 mr-1' /> VAX</Button>
                  <Button size='sm' variant='ghost' onClick={() => remove(d.id)} data-testid={`delete-dog-${d.id}`}><Trash2 className='w-4 h-4 text-[#B42318]' /></Button>
                </div>
                <div className='flex gap-2 mt-2'>
                  <Button
                    size='sm'
                    variant='outline'
                    className='flex-1 border-[#1B3A87]/30 text-[#1B3A87] dark:text-[#93C5FD] dark:border-[#93C5FD]/30 hover:bg-[#1B3A87]/5'
                    onClick={() => setExpandedDogId(expandedDogId === d.id ? null : d.id)}
                    data-testid={`view-clips-${d.id}`}
                  >
                    <FileVideo className='w-4 h-4 mr-1' />
                    {expandedDogId === d.id ? 'Hide Clips' : 'View Clips'}
                    {expandedDogId === d.id ? <ChevronUp className='w-3.5 h-3.5 ml-1' /> : <ChevronDown className='w-3.5 h-3.5 ml-1' />}
                  </Button>
                  {d.assigned_trainer_id && (
                    <Button
                      size='sm'
                      variant='outline'
                      className='border-[#C8102E]/30 text-[#C8102E] hover:bg-[#C8102E]/5'
                      onClick={() => navigate(`/messages?with=${d.assigned_trainer_id}`)}
                      data-testid={`message-trainer-${d.id}`}
                    >
                      <MessageCircle className='w-4 h-4' />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {expandedDogId && (
        <div className='mt-6' data-testid={`dog-clips-section-${expandedDogId}`}>
          <DogClipGallery dog={dogs.find((x) => x.id === expandedDogId)} />
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className='max-w-3xl max-h-[92vh] overflow-y-auto'>
          <DialogHeader><DialogTitle>{editingId ? 'Edit Dog Profile' : 'Add a Dog'}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className='space-y-4'>

            <FormSection icon={IdCard} title='Basics'>
              <div className='grid sm:grid-cols-3 gap-3'>
                <div className='sm:col-span-3'>
                  <Label>Photo</Label>
                  <div className='flex items-center gap-3 mt-1'>
                    {form.photo_base64 && <img src={form.photo_base64} alt='preview' className='w-16 h-16 rounded-lg object-cover border border-slate-200' />}
                    <Input type='file' accept='image/*' onChange={onPhoto} data-testid='dog-photo-input' />
                  </div>
                </div>
                <div><Label>Name *</Label><Input required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid='dog-name-input' /></div>
                <div><Label>Breed</Label><Input value={form.breed} onChange={(e)=>setForm({...form,breed:e.target.value})} data-testid='dog-breed-input' /></div>
                <div><Label>Color</Label><Input value={form.color} onChange={(e)=>setForm({...form,color:e.target.value})} data-testid='dog-color-input' /></div>
                <div><Label>Age (years)</Label><Input type='number' step='0.1' value={form.age_years} onChange={(e)=>setForm({...form,age_years:e.target.value})} data-testid='dog-age-input' /></div>
                <div><Label>Weight (lbs)</Label><Input type='number' step='0.1' value={form.weight_lbs} onChange={(e)=>setForm({...form,weight_lbs:e.target.value})} data-testid='dog-weight-input' /></div>
                <div><Label>Sex</Label><select className='w-full h-10 rounded-md border border-slate-200 px-3 text-sm' value={form.sex} onChange={(e)=>setForm({...form,sex:e.target.value})} data-testid='dog-sex-select'><option value='M'>Male</option><option value='F'>Female</option></select></div>
                <div><Label>Spay / Neuter</Label><select className='w-full h-10 rounded-md border border-slate-200 px-3 text-sm' value={form.spay_neuter} onChange={(e)=>setForm({...form,spay_neuter:e.target.value})} data-testid='dog-spay-select'><option value='intact'>Intact</option><option value='spayed'>Spayed</option><option value='neutered'>Neutered</option></select></div>
                <div><Label>Microchip #</Label><Input value={form.microchip_number} onChange={(e)=>setForm({...form,microchip_number:e.target.value})} placeholder='15 digits' data-testid='dog-microchip-input' /></div>
                <div><Label>Registration #</Label><Input value={form.registration_number} onChange={(e)=>setForm({...form,registration_number:e.target.value})} placeholder='AKC etc.' data-testid='dog-reg-input' /></div>
              </div>
            </FormSection>

            <FormSection icon={Pill} title='Health & Diet' accent='#C8102E'>
              <div className='grid sm:grid-cols-2 gap-3'>
                <div><Label>Allergies</Label><Textarea rows={2} value={form.allergies} onChange={(e)=>setForm({...form,allergies:e.target.value})} placeholder='Chicken, grain, bee stings…' data-testid='dog-allergies-input' /></div>
                <div><Label>Medications</Label><Textarea rows={2} value={form.medications} onChange={(e)=>setForm({...form,medications:e.target.value})} placeholder='Heartworm Rx, joint supplement…' data-testid='dog-meds-input' /></div>
                <div className='sm:col-span-2'><Label><Salad className='w-3 h-3 inline mr-1' /> Dietary notes</Label><Textarea rows={2} value={form.dietary_notes} onChange={(e)=>setForm({...form,dietary_notes:e.target.value})} placeholder='Brand of food, feeding schedule…' data-testid='dog-diet-input' /></div>
              </div>
            </FormSection>

            <FormSection icon={Stethoscope} title='Veterinarian' accent='#167A5A'>
              <div className='grid sm:grid-cols-2 gap-3'>
                <div><Label>Vet Name</Label><Input value={form.vet_name} onChange={(e)=>setForm({...form,vet_name:e.target.value})} data-testid='dog-vet-name-input' /></div>
                <div><Label>Vet Phone</Label><Input value={form.vet_phone} onChange={(e)=>setForm({...form,vet_phone:e.target.value})} data-testid='dog-vet-phone-input' /></div>
                <div className='sm:col-span-2'><Label>Vet Address</Label><Input value={form.vet_address} onChange={(e)=>setForm({...form,vet_address:e.target.value})} data-testid='dog-vet-addr-input' /></div>
              </div>
            </FormSection>

            <FormSection icon={Heart} title='Emergency Contact for this dog' accent='#B45309'>
              <div className='grid sm:grid-cols-3 gap-3'>
                <div><Label>Name</Label><Input value={form.emergency_contact_name} onChange={(e)=>setForm({...form,emergency_contact_name:e.target.value})} data-testid='dog-ec-name-input' /></div>
                <div><Label>Phone</Label><Input value={form.emergency_contact_phone} onChange={(e)=>setForm({...form,emergency_contact_phone:e.target.value})} data-testid='dog-ec-phone-input' /></div>
                <div><Label>Relationship</Label><Input value={form.emergency_contact_relationship} onChange={(e)=>setForm({...form,emergency_contact_relationship:e.target.value})} data-testid='dog-ec-rel-input' /></div>
              </div>
            </FormSection>

            <FormSection icon={IdCard} title='Behavior & Training notes' accent='#1B3A87'>
              <div className='grid sm:grid-cols-2 gap-3'>
                <div><Label>Behavior</Label><Textarea rows={3} value={form.behavior_notes} onChange={(e)=>setForm({...form,behavior_notes:e.target.value})} data-testid='dog-behavior-input' /></div>
                <div><Label>Training Needs / Goals</Label><Textarea rows={3} value={form.training_needs} onChange={(e)=>setForm({...form,training_needs:e.target.value})} data-testid='dog-training-input' /></div>
              </div>
            </FormSection>

            <DialogFooter><Button type='submit' className='bg-[#1B3A87] hover:bg-[#16306F] text-white' data-testid='dog-submit-button'>{editingId ? 'Save Changes' : 'Save Dog'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!vaxOpen} onOpenChange={(v)=> !v && setVaxOpen(null)}>
        <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader><DialogTitle>Vaccination Records</DialogTitle></DialogHeader>
          <form onSubmit={submitVax} className='grid sm:grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg'>
            <div><Label>Vaccine</Label>
              <select className='w-full h-10 rounded-md border border-slate-200 px-3 text-sm' value={vaxForm.vaccine_name} onChange={(e)=>setVaxForm({...vaxForm,vaccine_name:e.target.value})} data-testid='vax-name-select'>
                {['Rabies','DHPP','Bordetella','Leptospirosis','Canine Influenza'].map(v=> <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div><Label>Veterinarian</Label><Input value={vaxForm.veterinarian} onChange={(e)=>setVaxForm({...vaxForm,veterinarian:e.target.value})} data-testid='vax-vet-input' /></div>
            <div><Label>Administered</Label><Input required type='date' value={vaxForm.administered_date} onChange={(e)=>setVaxForm({...vaxForm,administered_date:e.target.value})} data-testid='vax-admin-date' /></div>
            <div><Label>Expires</Label><Input required type='date' value={vaxForm.expiration_date} onChange={(e)=>setVaxForm({...vaxForm,expiration_date:e.target.value})} data-testid='vax-exp-date' /></div>
            <div className='sm:col-span-2'><Label>Document (optional)</Label><Input type='file' accept='image/*,application/pdf' onChange={onVaxDoc} data-testid='vax-doc-input' /></div>
            <div className='sm:col-span-2'><Button type='submit' className='w-full bg-[#1B3A87] hover:bg-[#16306F] text-white' data-testid='vax-submit-button'>Save Vaccination</Button></div>
          </form>
          <div className='mt-4'>
            <div className='text-xs uppercase tracking-wider font-bold text-slate-500 mb-2'>On file ({vaxList.length})</div>
            {vaxList.length === 0 ? <div className='text-sm text-slate-500'>No records yet.</div> : (
              <div className='space-y-2'>
                {vaxList.map(v => {
                  const isExpired = new Date(v.expiration_date) < new Date();
                  return (
                    <div key={v.id} className={`p-3 rounded-lg border ${isExpired ? 'border-[#B42318]/30 bg-[#B42318]/5' : 'border-slate-200'}`}>
                      <div className='flex items-center justify-between'>
                        <div>
                          <div className='font-semibold text-sm'>{v.vaccine_name}</div>
                          <div className='text-xs text-slate-500'>Admin: {v.administered_date} · Expires: {v.expiration_date} {v.veterinarian && `· ${v.veterinarian}`}</div>
                        </div>
                        <div className='flex items-center gap-2'>
                          {isExpired ? <Badge className='bg-[#B42318] text-white'>Expired</Badge> : <Badge className='bg-[#167A5A] text-white'>Active</Badge>}
                          <Button size='sm' variant='ghost' onClick={() => removeVax(v.id)}><Trash2 className='w-4 h-4 text-[#B42318]' /></Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
